import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { isCalendarDate } from '../check-ins/schema';

// ─── urine drug test result vocabulary ──────────────────────────────────────
//
// One urine drug screen result is one substance with one outcome, recorded for
// the calendar day the sample was taken. The two value sets are 1.0's
// `test_results_urine.screenFor` and `.result` enums, kept verbatim so a
// migrated row and a Galapagos row speak the same vocabulary. They are the
// single source for the table's CHECK constraints, the request and response
// schemas and the Atlas form options (ADR-056 Rule 8).

export const URINE_TEST_SCREEN_FOR = ['opioids', 'cannabis', 'stimulants'] as const;
export const urineTestScreenForSchema = z.enum(URINE_TEST_SCREEN_FOR);
export type UrineTestScreenFor = z.infer<typeof urineTestScreenForSchema>;

export const URINE_TEST_RESULTS = ['positive', 'negative', 'invalid'] as const;
export const urineTestResultValueSchema = z.enum(URINE_TEST_RESULTS);
export type UrineTestResultValue = z.infer<typeof urineTestResultValueSchema>;

/**
 * Upper bound on a new result's notes, after trimming. 1.0 capped notes at 200
 * characters in the Atlas form only and not at all on the server; a migrated
 * note longer than this is still returned in full, because the bound governs
 * new writes, never the history.
 */
export const URINE_TEST_NOTES_MAX = 1000;

// ─── which programme a substance belongs to ─────────────────────────────────
//
// Keyed by `program_definitions.key`. A server-owned map for the same reason
// `QUIT_DATE_PROGRAM_KEYS` is one: the binding between a programme and the
// substance its screens test for is a reviewed diff, not a client guess. Atlas
// reads it to decide which programme tabs carry the section, and the server
// reads it to check that a member is enrolled in the programme a result is
// recorded under.

export const URINE_TEST_SCREEN_FOR_BY_PROGRAM_KEY = Object.freeze({
  opioid: 'opioids',
  cannabis: 'cannabis',
  stimulants: 'stimulants',
} as const satisfies Record<string, UrineTestScreenFor>);

/**
 * The substance a programme's urine screens test for, or `null` when the
 * programme has none. FAILS CLOSED: anything that is not exactly a key of the
 * map (absent, padded, differently cased, inherited) is `null`.
 */
export function urineTestScreenForProgram(
  programKey: string | null | undefined,
): UrineTestScreenFor | null {
  if (typeof programKey !== 'string') return null;
  const entry = Object.entries(URINE_TEST_SCREEN_FOR_BY_PROGRAM_KEY).find(
    ([key]) => key === programKey,
  );
  return entry ? entry[1] : null;
}

/** The programme keys whose enrolment a result for this substance requires. */
export function urineTestProgramKeysFor(screenFor: UrineTestScreenFor): readonly string[] {
  return Object.entries(URINE_TEST_SCREEN_FOR_BY_PROGRAM_KEY)
    .filter(([, substance]) => substance === screenFor)
    .map(([key]) => key);
}

// ─── write refusal vocabulary ───────────────────────────────────────────────
//
// The `error.code` values a record call may return beyond the generic
// `invalid_request` (ADR-002 Rule 4). `not_enrolled_in_programme` is the same
// string NRT and quit dates return for the same situation: the member has no
// active episode of the programme the result belongs to.

export const URINE_TEST_RESULT_ERROR_CODES = {
  /** `testDate` is later than the member-local current day. */
  futureTestDate: 'future_test_date',
  /** No active enrolment in the programme that maps to `screenFor`. */
  notEnrolledInProgramme: 'not_enrolled_in_programme',
  /** A result for this member, substance and day already exists, native or migrated. */
  resultExists: 'urine_test_result_exists',
  /**
   * The member was migrated and their Pelago 1 urine results have not been
   * imported yet. A write now could pass the duplicate check and then meet an
   * imported row for the same substance and day, so recording waits for it.
   */
  historyPending: 'urine_test_result_history_pending',
  /**
   * The member was migrated and their Pelago 1 urine import ran and failed. It
   * waits for a person to repair and replay it, and a replay may still import
   * a row for the same substance and day, so recording waits for that too.
   */
  historyImportFailed: 'urine_test_result_history_import_failed',
} as const;

export type UrineTestResultErrorCode =
  (typeof URINE_TEST_RESULT_ERROR_CODES)[keyof typeof URINE_TEST_RESULT_ERROR_CODES];

// ─── what the migrated part of a history means ──────────────────────────────
//
// Resolved the same way as the emergency-details block state: the migration
// marker is the only authority for "was this member in 1.0 at all", and the
// Tier 2 step status says whether the import has run.

export const URINE_TEST_LEGACY_HISTORY_STATES = [
  /** The member was migrated and the 1.0 urine results import has run. */
  'imported',
  /**
   * The member was migrated and the 1.0 urine results import has not settled
   * yet: it has not run, is running, or its step has a status that says neither
   * done nor failed.
   */
  'import_pending',
  /**
   * The member was migrated and the 1.0 urine results import ran and failed;
   * it needs a person to repair and replay it. The rows shown may not be all.
   */
  'import_failed',
  /** The member never existed in 1.0; every result is a Galapagos one. */
  'not_migrated',
] as const;

export const urineTestLegacyHistoryStateSchema = z.enum(URINE_TEST_LEGACY_HISTORY_STATES);
export type UrineTestLegacyHistoryState = z.infer<typeof urineTestLegacyHistoryStateSchema>;

/**
 * The cannot-say state, CLIENT-ONLY. An older Atlas meeting a newer state must
 * say nothing about the import rather than claim it is pending or done.
 */
export const URINE_TEST_LEGACY_HISTORY_STATE_UNRECOGNISED = 'unrecognised';

export type UrineTestLegacyHistoryClientState =
  | UrineTestLegacyHistoryState
  | typeof URINE_TEST_LEGACY_HISTORY_STATE_UNRECOGNISED;

const isLegacyHistoryState = (value: string): value is UrineTestLegacyHistoryState =>
  URINE_TEST_LEGACY_HISTORY_STATES.some((state) => state === value);

/**
 * Forward-tolerant read (ADR-135): an unknown STRING resolves to the cannot-say
 * state; anything that is not a string is still a malformed payload and fails.
 */
export const urineTestLegacyHistoryStateClientSchema = z
  .string()
  .transform(
    (value): UrineTestLegacyHistoryClientState =>
      isLegacyHistoryState(value) ? value : URINE_TEST_LEGACY_HISTORY_STATE_UNRECOGNISED,
  );

// ─── wire shapes ────────────────────────────────────────────────────────────

/** A calendar day (`YYYY-MM-DD`) with no time and no zone. */
export const urineTestDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a YYYY-MM-DD date')
  .refine(isCalendarDate, 'must be a real calendar date');

/**
 * The record request. Strict (ADR-135 Rule 1): the recorder comes from the
 * verified staff subject, so a body naming one is refused rather than ignored.
 * The notes bound is applied after trimming by the service, so the schema only
 * bounds the raw length generously enough to let whitespace through.
 */
export const recordUrineTestResultRequestSchema = z
  .object({
    testDate: urineTestDaySchema,
    screenFor: urineTestScreenForSchema,
    result: urineTestResultValueSchema,
    notes: z
      .string()
      .nullable()
      .optional()
      .refine((value) => value == null || value.trim().length <= URINE_TEST_NOTES_MAX, {
        message: `notes must be at most ${URINE_TEST_NOTES_MAX} characters`,
      }),
  })
  .strict();

export type RecordUrineTestResultRequest = z.infer<typeof recordUrineTestResultRequestSchema>;

const urineTestResultRowBase = {
  testDate: urineTestDaySchema,
  screenFor: urineTestScreenForSchema,
  result: urineTestResultValueSchema,
  notes: z.string().nullable(),
};

/** A result recorded in Galapagos. */
export const galapagosUrineTestResultRowSchema = z.object({
  ...urineTestResultRowBase,
  origin: z.literal('galapagos'),
  id: z.string().uuid(),
  recordedAt: z.string(),
});

/**
 * A result migrated from Pelago 1.0. It carries no `id` (the mirror's surrogate
 * key is an import artefact, not a record identity) and no `recordedAt` (1.0
 * never recorded who or when beyond the import).
 */
export const pelago1UrineTestResultRowSchema = z.object({
  ...urineTestResultRowBase,
  origin: z.literal('pelago1'),
});

export const urineTestResultRowSchema = z.discriminatedUnion('origin', [
  galapagosUrineTestResultRowSchema,
  pelago1UrineTestResultRowSchema,
]);

export type UrineTestResultRow = z.infer<typeof urineTestResultRowSchema>;
export type GalapagosUrineTestResultRow = z.infer<typeof galapagosUrineTestResultRowSchema>;
export type Pelago1UrineTestResultRow = z.infer<typeof pelago1UrineTestResultRowSchema>;

/**
 * A day on which Pelago 1.0 holds a result for the requested substance whose
 * outcome does not parse. The row itself is never shown (its outcome would be
 * a guess), but it still holds the day against a second result, so the
 * history names the day to make that refusal visible. It carries only the day.
 */
export const unreadableLegacyResultSchema = z.object({ testDate: urineTestDaySchema });

export type UnreadableLegacyResult = z.infer<typeof unreadableLegacyResultSchema>;

export const urineTestResultsListDataSchema = z.object({
  /** Newest `testDate` first; Galapagos rows before Pelago 1 rows on the same day. */
  results: z.array(urineTestResultRowSchema),
  /** One entry per day, newest first. */
  unreadableLegacyResults: z.array(unreadableLegacyResultSchema),
  legacyHistoryState: urineTestLegacyHistoryStateSchema,
  /**
   * The member's own current day in their stored zone (ADR-095), the latest
   * `testDate` a record call accepts. A client defaults and bounds its date
   * input with this rather than its own clock, because the clinician's day and
   * the member's can differ.
   */
  memberToday: urineTestDaySchema,
});

export type UrineTestResultsListData = z.infer<typeof urineTestResultsListDataSchema>;

export const urineTestResultsListResponseSchema = apiEnvelope(urineTestResultsListDataSchema);

export const recordUrineTestResultResponseSchema = apiEnvelope(galapagosUrineTestResultRowSchema);

// ─── client reads (Atlas) ───────────────────────────────────────────────────
//
// Identical to the wire shapes but for forward tolerance: an unknown
// `legacyHistoryState` resolves to the cannot-say state, and a row this build
// cannot name (an unknown `origin`, or a `screenFor` or `result` outside its
// vocabulary) is dropped and counted rather than failing the whole history.
// Derived from the wire rows so the two cannot drift on any field.

const KNOWN_ORIGINS: readonly string[] = ['galapagos', 'pelago1'];

const hasUnknownValue = (row: { screenFor: string; result: string }): boolean =>
  !URINE_TEST_SCREEN_FOR.some((known) => known === row.screenFor) ||
  !URINE_TEST_RESULTS.some((known) => known === row.result);

/** The two vocabulary fields widened to any string, for the tolerant arms. */
const NEWER_VOCABULARY = { screenFor: z.string(), result: z.string() };

// A row is dropped only when its vocabulary is newer than this build. Every
// other field keeps its wire validation, so a row with a missing or malformed
// field still fails the parse: that is a malformed payload, not a newer
// vocabulary, and must surface as an error. The exact wire rows are tried
// first, so a row this build knows is never dropped.
const clientRowSchema = z.union([
  galapagosUrineTestResultRowSchema,
  pelago1UrineTestResultRowSchema,
  galapagosUrineTestResultRowSchema
    .extend(NEWER_VOCABULARY)
    .refine(hasUnknownValue)
    .transform(() => null),
  pelago1UrineTestResultRowSchema
    .extend(NEWER_VOCABULARY)
    .refine(hasUnknownValue)
    .transform(() => null),
  z
    .object({ origin: z.string().refine((origin) => !KNOWN_ORIGINS.includes(origin)) })
    .transform(() => null),
]);

export const urineTestResultsListClientDataSchema = z
  .object({
    results: z.array(clientRowSchema),
    legacyHistoryState: urineTestLegacyHistoryStateClientSchema,
    memberToday: urineTestDaySchema,
    // Protects a newer Atlas reading an older API that does not send the field
    // yet; an older Atlas reading this API simply strips it.
    unreadableLegacyResults: z.array(unreadableLegacyResultSchema).default([]),
  })
  .transform(({ results, ...rest }) => {
    const shown = results.filter((row): row is UrineTestResultRow => row !== null);
    return { ...rest, results: shown, hiddenRowCount: results.length - shown.length };
  });

export type UrineTestResultsListClientData = z.infer<typeof urineTestResultsListClientDataSchema>;

export const urineTestResultsListClientResponseSchema = apiEnvelope(
  urineTestResultsListClientDataSchema,
);
